import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import SEO from '../components/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
      setStatus('confirm');
    } else {
      setStatus('error');
      setMessage('Invalid unsubscribe link. Please use the link from your email.');
    }
  }, [searchParams]);

  const handleUnsubscribe = async () => {
    setStatus('loading');
    try {
      const response = await axios.post(`${API}/newsletter/unsubscribe`, { email });
      setStatus('success');
      setMessage(response.data.message || 'You have been successfully unsubscribed.');
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <SEO 
        title="Unsubscribe from Newsletter"
        description="Unsubscribe from TruckAccidentSupport.com newsletter"
        canonical="/unsubscribe"
      />

      <Card className="w-full max-w-md bg-gray-900 border-gray-800">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-orange-500/10">
              {status === 'success' ? (
                <CheckCircle className="h-8 w-8 text-green-400" />
              ) : status === 'error' ? (
                <AlertCircle className="h-8 w-8 text-red-400" />
              ) : (
                <Mail className="h-8 w-8 text-orange-400" />
              )}
            </div>
          </div>
          <CardTitle className="text-white text-2xl">
            {status === 'success' && 'Unsubscribed Successfully'}
            {status === 'confirm' && 'Unsubscribe from Newsletter'}
            {status === 'error' && 'Error'}
            {status === 'loading' && 'Processing...'}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {status === 'confirm' && (
            <>
              <CardDescription className="text-gray-400 text-center">
                Are you sure you want to unsubscribe <strong className="text-white">{email}</strong> from our newsletter?
              </CardDescription>
              <p className="text-sm text-gray-500 text-center">
                You'll no longer receive safety alerts, updates, and educational content from TruckAccidentSupport.com
              </p>
              <div className="flex space-x-3 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1 border-gray-700 text-gray-300"
                  onClick={() => window.location.href = '/'}
                  data-testid="unsubscribe-cancel-btn"
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                  onClick={handleUnsubscribe}
                  data-testid="unsubscribe-confirm-btn"
                >
                  Unsubscribe
                </Button>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <CardDescription className="text-gray-300 text-center">
                {message}
              </CardDescription>
              <p className="text-sm text-gray-500 text-center">
                We're sorry to see you go. You can always subscribe again from our homepage.
              </p>
              <Button 
                className="w-full btn-primary text-white mt-4"
                onClick={() => window.location.href = '/'}
                data-testid="return-home-btn"
              >
                Return to Homepage
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <CardDescription className="text-red-400 text-center">
                {message}
              </CardDescription>
              <Button 
                className="w-full btn-primary text-white mt-4"
                onClick={() => window.location.href = '/contact'}
                data-testid="contact-support-btn"
              >
                Contact Support
              </Button>
            </>
          )}

          {status === 'loading' && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Unsubscribe;
